/* AA Ring Configurator v1. Native paid fee variants; no client-side price overrides. */
export function resolveRingConfiguration(data, state, variant) {
  if (!variant) throw new Error("Choose an available ring size and finish.");
  if (data.engravingAllowed === false || data.settings.engraving_enabled === false) state = {...state, engraving:false, inside:"", outside:""};
  const number = value => Number(value || 0);
  const option = (v, n) => v["option" + n] || v.options?.[n - 1] || v.selectedOptions?.[n - 1]?.value || "";
  const material = option(variant, 1);
  const size = option(variant, 2);
  const color = /rose/i.test(material) ? "rose" : /gold/i.test(material) ? "yellow" : "white";
  const solid = state.metal !== "silver";
  const moissanite = solid || state.stone === "moissanite";
  const additions = [];
  const take = (v, label, details = {}) => {
    if (!v || v.available === false) throw new Error("This customization is temporarily unavailable. Please choose another option.");
    additions.push({id: number(v.id), price: number(v.price), label, details});
    return v;
  };
  const byTitle = (list, title) => (list || []).find(v => v.title === title);
  let metal = material;
  if (solid) {
    const karat = state.metal === "14k" ? "14K" : "18K";
    metal = karat + " solid " + color + " gold";
    take(byTitle(data.upgrades, karat + " Solid Gold"), metal + " upgrade", {Metal: metal, Stone: "Moissanite included"});
  } else if (moissanite) {
    const sourceMaterial = data.settings.source_material_by_finish?.[material] || (color === "white" ? "S925 Sterling Silver" : color === "rose" ? "18K Rose Gold Vermeil" : "18K Gold Vermeil");
    const sizeIndex = Number(data.settings.source_size_option_index || 2);
    const matchesSource = v => option(v, 1) === sourceMaterial && (!data.settings.source_carat_option_index || option(v, Number(data.settings.source_carat_option_index)) === data.settings.source_carat_value);
    const source = (data.sourceVariants || []).find(v => matchesSource(v) && option(v, sizeIndex) === size)
      || (data.sourceVariants || []).find(matchesSource);
    if (!source) throw new Error("The moissanite option is temporarily unavailable.");
    const difference = number(source.price) - number(variant.price);
    const fee = data.currency === "USD"
      ? (data.upgrades || []).find(v => /^Moissanite/.test(v.title) && Math.abs(number(v.price) - difference) <= 2)
      : byTitle(data.upgrades, data.settings.moissanite_fee_title_by_material?.[material]);
    if (difference < 0 || !fee) throw new Error("The moissanite price needs updating. Please contact our team.");
    take(fee, "Moissanite upgrade", {Stone: "Moissanite", Finish: color === "white" ? "2.5-micron platinum plating included" : "2.5-micron " + color + "-gold vermeil included"});
    metal = color === "white" ? "S925 silver · 2.5-micron platinum" : material;
  }
  const increment = moissanite ? number(state.carat) : 0;
  if (increment > 0) take((data.carats || []).find(v => parseInt(v.title.replace("+", ""), 10) === increment), "Larger moissanite stone", {"Center stone": (number(data.settings.base_carat) + increment) + "CT total (+" + increment + "CT)"});
  if (state.engraving) {
    const title = state.placement === "inside" ? "Inside of the band" : state.placement === "outside" ? "Outside of the band" : "Inside & outside of the band";
    const details = {Placement: state.placement === "both" ? "Inside and outside" : state.placement};
    if (state.placement !== "outside") details["Inside engraving"] = state.inside.trim();
    if (state.placement !== "inside") details["Outside engraving"] = state.outside.trim();
    take(byTitle(data.engraving, title), "Engraving", details);
  }
  if (state.colored) {
    const fee = moissanite ? (data.colorsFee || [])[0] : byTitle(data.upgrades, data.settings.zirconia_color_fee_title || "Zirconia Stone Color");
    take(fee, "Stone color", {Color: state.color || "Choose a color", Placement: state.scope === "all" ? "Center and accent stones" : "Center stone only", Stone: moissanite ? "Moissanite" : "Cubic zirconia"});
  }
  if (state.giftbox) take(data.giftbox, "LED light giftbox");
  const stoneSize = (number(data.settings.base_carat) + increment) + "CT";
  const properties = {
    Metal: metal,
    "Ring size": size,
    Stone: moissanite ? "Moissanite" : "Cubic zirconia",
    "Center stone": stoneSize,
    "Stone shape": data.settings.stone_shape || "Oval",
    "Stone color": state.colored ? (state.color || "Choose a color") : (data.settings.base_stone_color || "White")
  };
  if (state.colored) properties["Color placement"] = state.scope === "all" ? "Center and accent stones" : "Center stone only";
  if (state.engraving && state.placement !== "outside") properties["Inside engraving"] = state.inside.trim();
  if (state.engraving && state.placement !== "inside") properties["Outside engraving"] = state.outside.trim();
  if (state.giftbox) properties["Gift box"] = "Velvet LED light giftbox";
  return {variantId: number(variant.id), base: number(variant.price), additions, total: number(variant.price) + additions.reduce((sum, x) => sum + x.price, 0), properties, metal, moissanite, stoneSize, color};
}
export function buildRingCheckoutFields(resolved, quantity, title, group) {
  if (!resolved) return [];
  const label = title.replace(/ - From .*/, "");
  const expected = resolved.additions.map(a => ({id:a.id,label:a.label,details:a.details}));
  const properties = {...resolved.properties,_aarc_group:group,_aarc_role:"ring",_aarc_expected:JSON.stringify(expected),_bsw_label:label};
  const fields = Object.entries(properties).map(([key,value]) => ["properties["+key+"]",String(value)]);
  resolved.additions.forEach((addition,index) => {
    const prefix = "shopify-additional-items["+(index+1)+"]";
    fields.push([prefix+"[id]",String(addition.id)],[prefix+"[quantity]",String(quantity)],[prefix+"[parent_id]",String(resolved.variantId)]);
    const child = {...addition.details,For:label,_aarc_group:group,_aarc_role:"addon",_bsw_label:addition.label+" · "+label};
    fields.push([prefix+"[properties]",JSON.stringify(Object.entries(child).map(([key,value]) => ({key,value:String(value)})))]);
  });
  return fields;
}
if (typeof document !== "undefined") {
  class AARingConfigurator extends HTMLElement {
    static states = new Map();
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.data = JSON.parse(this.querySelector("[data-aarc-config]").textContent);
      this.state = AARingConfigurator.states.get(this.data.productId) || {metal:"silver",stone:"zirconia",carat:0,engraving:false,placement:"inside",inside:"",outside:"",colored:false,color:"",scope:"center",giftbox:false};
      AARingConfigurator.states.set(this.data.productId, this.state);
      this.busy = false;
      this.abort = new AbortController();
      this.$ = s => this.querySelector(s);
      this.$$ = s => Array.from(this.querySelectorAll(s));
      this.section = document.getElementById("shopify-section-" + this.dataset.section);
      this.form = this.section?.querySelector('product-form form[action*="/cart/add"]');
      if (!this.form) { this.$("[data-error]").textContent="Personalization could not load. Please refresh the page.";this.$("[data-error]").hidden=false;return; }
      const input = this.form.querySelector('[name="id"]');
      this.variant = this.data.variants.find(v => Number(v.id) === Number(input?.value)) || this.data.variants[0];
      const signal = this.abort.signal;
      document.addEventListener("variant:change", e => {
        if (e.target !== this.form && !this.section.contains(e.target)) return;
        this.variant = this.data.variants.find(v => Number(v.id) === Number(e.detail?.variant?.id)) || this.variant;
        this.render();
      }, {signal});
      this.addEventListener("change", e => {this.update(e.target);this.render();}, {signal});
      this.addEventListener("input", e => {
        if (e.target.matches("[data-inside],[data-outside]")) {this.update(e.target);this.render();}
      }, {signal});
      this.$("[data-reset]").addEventListener("click", () => {
        Object.assign(this.state,{metal:"silver",metalChoice:"14k",stone:"zirconia",carat:0,engraving:false,placement:"inside",inside:"",outside:"",colored:false,color:"",scope:"center",giftbox:false});
        this.$$('[type="checkbox"]').forEach(el=>el.checked=false);
        this.$('[data-engraving-placement][value="inside"]').checked=true;
        this.$$('[data-color]').forEach(el=>el.checked=false);
        this.$('[data-inside]').value="";this.$('[data-outside]').value="";this.$('[data-carat]').value="0";this.$('[data-color-scope]').value="center";
        this.render(); this.clearError();
      }, {signal});
      this.checkoutGroup = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36)+Math.random().toString(36).slice(2);
      this.checkoutFields = document.createElement("div");
      this.checkoutFields.hidden = true;
      this.checkoutFields.dataset.aarcCheckoutFields = "";
      this.form.appendChild(this.checkoutFields);
      const isWalletEvent = e => {
        const wallet = this.form.querySelector("[data-shopify=payment-button]");
        return wallet && (e.composedPath().includes(wallet) || wallet.contains(e.submitter));
      };
      document.addEventListener("click", e => {
        if (!isWalletEvent(e)) return;
        const input = this.form.querySelector('[name="id"]');
        this.variant = this.data.variants.find(v => Number(v.id) === Number(input?.value)) || this.variant;
        this.render();
        if (this.busy || !this.valid()) {e.preventDefault();e.stopImmediatePropagation();return;}
        this.checkoutGroup = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36)+Math.random().toString(36).slice(2);
        this.syncCheckoutFields();
      }, {capture:true,signal});
      document.addEventListener("submit", e => {
        if(e.target!==this.form)return;
        if(isWalletEvent(e)) {
          this.render();
          if(this.busy || !this.valid()){e.preventDefault();e.stopImmediatePropagation();}
          return;
        }
        e.preventDefault();e.stopImmediatePropagation();
        this.purchase(false);
      }, {capture:true,signal});
      const quantityChanged = e => {
        if(e.target.name==="quantity" && e.target.form===this.form)this.syncCheckoutFields();
      };
      document.addEventListener("change",quantityChanged,{signal});
      document.addEventListener("input",quantityChanged,{signal});
      this.render();
    }
    disconnectedCallback(){this.abort?.abort();this.checkoutFields?.remove();this.initialized=false;}
    syncCheckoutFields(){
      if(!this.checkoutFields)return;
      const quantityInput = this.form.elements.namedItem("quantity");
      const quantity = Math.max(1,parseInt(quantityInput?.value||"1",10)||1);
      const fields = buildRingCheckoutFields(this.resolved,quantity,this.data.title,this.checkoutGroup);
      const fragment = document.createDocumentFragment();
      for(const [name,value] of fields){
        const input=document.createElement("input");input.type="hidden";input.name=name;input.value=value;fragment.append(input);
      }
      this.checkoutFields.replaceChildren(fragment);
    }
    money(cents){return new Intl.NumberFormat(this.data.locale||"en",{style:"currency",currency:this.data.currency||"USD"}).format(cents/100);}
    update(el){
      const s=this.state;
      if(el.matches("[data-metal-enabled]"))s.metal=el.checked?(s.metalChoice||"14k"):"silver";
      if(el.matches("[data-metal]")){s.metal=el.value;s.metalChoice=el.value;}
      if(el.matches("[data-gemstones-enabled]")&&s.metal==="silver")s.stone=el.checked?"moissanite":"zirconia";
      if(el.matches("[data-carat]"))s.carat=Number(el.value);
      if(el.matches("[data-engraving-enabled]"))s.engraving=el.checked;
      if(el.matches("[data-engraving-placement]"))s.placement=el.value;
      if(el.matches("[data-inside]"))s.inside=el.value;
      if(el.matches("[data-outside]"))s.outside=el.value;
      if(el.matches("[data-color-enabled]"))s.colored=el.checked;
      if(el.matches("[data-color]"))s.color=el.value;
      if(el.matches("[data-color-scope]"))s.scope=el.value;
      if(el.matches("[data-giftbox]"))s.giftbox=el.checked;
      this.clearError();
    }
    clearError(){this.$("[data-error]").hidden=true;this.$("[data-status]").textContent="";}
    render(){
      const s=this.state,d=this.data;
      if (d.engravingAllowed === false || d.settings.engraving_enabled === false) {
        Object.assign(s,{engraving:false,inside:"",outside:""});
        this.$("[data-engraving-enabled]").disabled=true;
      }
      this.$$("[data-metal]").forEach(el=>el.checked=el.value===s.metal);
      this.$$("[data-engraving-placement]").forEach(el=>el.checked=el.value===s.placement);
      this.$$("[data-color]").forEach(el=>el.checked=el.value===s.color);
      this.$("[data-engraving-enabled]").checked=s.engraving;this.$("[data-color-enabled]").checked=s.colored;this.$("[data-giftbox]").checked=s.giftbox;
      this.$("[data-inside]").value=s.inside;this.$("[data-outside]").value=s.outside;this.$("[data-color-scope]").value=s.scope;
      try {
        this.resolved=resolveRingConfiguration(d,s,this.variant);
      } catch(err) {this.resolved=null;this.syncCheckoutFields();this.showError(err.message);return;}
      const r=this.resolved;
      const solid=s.metal!=="silver";
      const metalToggle=this.$("[data-metal-enabled]");
      metalToggle.checked=solid;metalToggle.setAttribute("aria-expanded",String(solid));
      this.$("[data-metal-panel]").hidden=!solid;
      for(const k of ["14k","18k"]){
        const v=d.upgrades.find(v=>v.title===(k==="14k"?"14K":"18K")+" Solid Gold");
        this.$('[data-metal-price="'+k+'"]').textContent=v?"+"+this.money(v.price):"Unavailable";
        this.$('[data-metal-title="'+k+'"]').textContent=(k==="14k"?"14K":"18K")+" solid "+r.color+" gold";
        this.$('[data-metal][value="'+k+'"]').disabled=!v||v.available===false;
      }
      const metalFee=d.upgrades.find(v=>v.title===(s.metal==="18k"?"18K":"14K")+" Solid Gold");
      this.$("[data-metal-toggle-price]").textContent=metalFee?(solid?"+":"From +")+this.money(metalFee.price):"Unavailable";
      let moFee;
      try {moFee=resolveRingConfiguration(d,{...s,metal:"silver",stone:"moissanite",carat:0,engraving:false,colored:false,giftbox:false},this.variant).additions[0];}catch(e){}
      const gemstoneToggle=this.$("[data-gemstones-enabled]");
      gemstoneToggle.checked=r.moissanite;gemstoneToggle.disabled=solid||!moFee;
      gemstoneToggle.setAttribute("aria-expanded",String(r.moissanite));
      this.$("[data-gemstone-toggle]").classList.toggle("aarc__toggle--included",solid);
      this.$("[data-gemstone-title]").textContent=solid?"Moissanite":"Upgrade gemstones";
      this.$("[data-gemstone-toggle-price]").textContent=solid?"Included":moFee?"+"+this.money(moFee.price):"Unavailable";
      this.$("[data-moissanite-price]").textContent=moFee?"+"+this.money(moFee.price):"Unavailable";
      this.$("[data-stone-note]").hidden=!solid;
      this.$("[data-gemstone-panel]").hidden=!r.moissanite;
      this.$("[data-moissanite-option]").hidden=solid;
      this.$("[data-carat-panel]").hidden=!r.moissanite;
      const carat=this.$("[data-carat]");
      if(!carat.options.length){
        const opt=document.createElement("option");opt.value="0";opt.textContent=d.settings.base_carat+"CT · Included";carat.append(opt);
        for(const v of d.carats||[]){
          const inc=parseInt(v.title.replace("+",""),10);if(!Number.isFinite(inc))continue;
          const o=document.createElement("option");o.value=String(inc);o.disabled=v.available===false;o.textContent=(Number(d.settings.base_carat)+inc)+"CT (+"+inc+"CT) · +"+this.money(v.price);carat.append(o);
        }
      }
      carat.value=String(s.carat);
      this.$("[data-engraving-panel]").hidden=!s.engraving;
      this.$("[data-inside-field]").hidden=s.placement==="outside";
      this.$("[data-outside-field]").hidden=s.placement==="inside";
      this.$("[data-inside]").required=s.engraving&&s.placement!=="outside";
      this.$("[data-outside]").required=s.engraving&&s.placement!=="inside";
      const titles={inside:"Inside of the band",outside:"Outside of the band",both:"Inside & outside of the band"};
      for(const p of Object.keys(titles)){
        const fee=(d.engraving||[]).find(v=>v.title===titles[p]);
        this.$('[data-eng-price="'+p+'"]').textContent=fee?"+"+this.money(fee.price):"Unavailable";
      }
      const engravingFee=(d.engraving||[]).find(v=>v.title===titles[s.engraving?s.placement:"inside"]);
      this.$("[data-engraving-price]").textContent=engravingFee?"+"+this.money(engravingFee.price):"Unavailable";
      this.$("[data-inside-count]").textContent=s.inside.length+" / "+d.settings.max_chars;
      this.$("[data-outside-count]").textContent=s.outside.length+" / "+d.settings.max_chars;
      this.$("[data-engraving-preview]").textContent=(s.placement!=="outside"?s.inside:"")+(s.placement==="both"&&s.inside&&s.outside?" · ":"")+(s.placement!=="inside"?s.outside:"")||"Your words here";
      this.$("[data-color-panel]").hidden=!s.colored;
      const colorFee=r.moissanite?d.colorsFee?.[0]:d.upgrades.find(v=>v.title===(d.settings.zirconia_color_fee_title||"Zirconia Stone Color"));
      this.$("[data-color-price]").textContent=colorFee?"+"+this.money(colorFee.price):"Unavailable";
      this.$("[data-box-price]").textContent=d.giftbox?"+"+this.money(d.giftbox.price):"Unavailable";
      this.$("[data-giftbox]").disabled=!d.giftbox||d.giftbox.available===false;
      this.$("[data-selection-description]").textContent=r.metal+" · "+r.properties.Stone+" · "+r.stoneSize+" · "+r.properties["Stone color"];
      const lines=this.$("[data-breakdown]");lines.replaceChildren();
      for(const item of [{label:"Base ring",price:r.base},...r.additions]){
        const li=document.createElement("li");const name=document.createElement("span"),price=document.createElement("span");
        name.textContent=item.label;price.textContent=this.money(item.price);li.append(name,price);lines.append(li);
      }
      this.$("[data-total]").textContent=this.money(r.total);
      this.syncCheckoutFields();
      this.dataset.total=String(r.total);
      this.dataset.stone=r.moissanite?"moissanite":"zirconia";
    }
    showError(message,focus){this.$("[data-error]").textContent=message;this.$("[data-error]").hidden=false;if(focus)focus.focus();}
    valid(){
      if(!this.resolved)return false;
      if(this.variant.available===false){this.showError("Choose an available ring size and finish.");return false;}
      if(this.state.engraving){
        for(const [key,show] of [["inside",this.state.placement!=="outside"],["outside",this.state.placement!=="inside"]]){
          if(show&&!this.state[key].trim()){this.showError("Enter your "+key+" engraving, or turn engraving off.",this.$("[data-"+key+"]"));return false;}
          if(this.state[key].length>Number(this.data.settings.max_chars)){this.showError("Keep each engraving within "+this.data.settings.max_chars+" characters.");return false;}
        }
      }
      if(this.state.colored&&!this.state.color){this.showError("Choose your stone color, or turn the color option off.",this.$("[data-color]"));return false;}
      return true;
    }
    async purchase(checkout){
      if(this.busy)return;
      const input=this.form.querySelector('[name="id"]');
      this.variant=this.data.variants.find(v=>Number(v.id)===Number(input?.value))||this.variant;
      this.render();if(!this.valid())return;
      const r=this.resolved;
      const group=(crypto.randomUUID?crypto.randomUUID():Date.now().toString(36)+Math.random().toString(36).slice(2));
      const root=window.Shopify?.routes?.root||"/";
      const quantity=Math.max(1,parseInt(this.form.querySelector('[name="quantity"]')?.value||"1",10));
      const label=this.data.title.replace(/ - From .*/,"");
      const expected=r.additions.map(a=>({id:a.id,label:a.label,details:a.details}));
      const properties={...r.properties,_aarc_group:group,_aarc_role:"ring",_aarc_expected:JSON.stringify(expected),_bsw_label:label};
      this.busy=true;
      const buttons=Array.from(this.form.querySelectorAll('[type="submit"]'));
      buttons.forEach(b=>b.setAttribute("aria-busy","true"));
      this.$("[data-status]").textContent="Adding your complete selection…";
      const call=async(path,payload)=>{
        const response=await fetch(root+path,{method:"POST",headers:{"Content-Type":"application/json","X-Requested-With":"XMLHttpRequest"},body:JSON.stringify(payload)});
        const data=await response.json();if(!response.ok)throw new Error(data.description||"Your selection could not be added. Please try again.");return data;
      };
      try {
        // Add the parent first to obtain an unambiguous key, even when the same variant already has another configuration.
        const result=await call("cart/add.js",{items:[{id:r.variantId,quantity,properties}]});
        const parent=result.items?.[0]||result;
        if(!parent.key)throw new Error("Your ring could not be added. Please try again.");
        if(expected.length)await call("cart/add.js",{items:expected.map(a=>({id:a.id,quantity,parent_line_key:parent.key,properties:{...a.details,For:label,_aarc_group:group,_aarc_role:"addon",_bsw_label:a.label+" · "+label}}))});
        const cart=await(await fetch(root+"cart.js")).json();
        const actual=cart.items.filter(x=>x.properties?._aarc_group===group);
        if(actual.length!==expected.length+1||expected.some(e=>!actual.some(a=>a.variant_id===e.id&&a.quantity===quantity)))throw new Error("The complete customization could not be added. Please try again.");
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh",{bubbles:true}));
        document.documentElement.dispatchEvent(new CustomEvent("cart:open",{bubbles:true}));
        const drawer=document.querySelector("cart-drawer");if(drawer&&typeof drawer.show==="function")drawer.show();else window.location.assign(root+"cart");
        this.$("[data-status]").textContent="Your complete selection is in the cart.";
      } catch(error) {
        // Compensate only this new group; leave all pre-existing customer items untouched.
        try {
          const cart=await(await fetch(root+"cart.js")).json();const updates={};
          cart.items.filter(x=>x.properties?._aarc_group===group).forEach(x=>updates[x.key]=0);
          if(Object.keys(updates).length)await call("cart/update.js",{updates});
          document.documentElement.dispatchEvent(new CustomEvent("cart:refresh",{bubbles:true}));
        }catch(rollbackError){this.showError("Please check your cart before trying again. Contact our team if your customization looks incomplete.");return;}
        this.showError(error.message);this.$("[data-status]").textContent="";
      } finally {
        this.busy=false;buttons.forEach(b=>b.removeAttribute("aria-busy"));
      }
    }
  }
  if(!customElements.get("aa-ring-configurator"))customElements.define("aa-ring-configurator",AARingConfigurator);
}
